import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { Depense, GroupeDepenses } from '../../types/ndf';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Champ,
  dateFr,
  Etiquette,
  Saisie,
} from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';
import { useNdfMoi } from '../../hooks/useNdfMoi';
import { euros, STATUTS_NOTE } from './NdfMesNotesPage';
import { PanneauAssociation } from '../../components/ndf/PanneauAssociation';
import { ChronologieNote } from '../../components/ndf/ChronologieNote';

/**
 * Détail d'une note de frais : ce qu'elle contient, ce qu'elle totalise.
 *
 * L'association se fait PAR GROUPE — client, mission, puis les non
 * refacturables. C'est ce qui permet de composer une note de douze dépenses en
 * trois gestes au lieu de douze cases à cocher, et c'est le critère de recette
 * du lot.
 */
export function NdfNoteDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [edition, setEdition] = useState(false);
  const [titre, setTitre] = useState('');
  const [ajout, setAjout] = useState(false);
  const [choisies, setChoisies] = useState<Set<string>>(new Set());
  const [confirmation, setConfirmation] = useState(false);
  const [soumission, setSoumission] = useState(false);
  const [confirmationReouverture, setConfirmationReouverture] = useState(false);
  const [vue, setVue] = useState<'ensemble' | 'chronologie'>('ensemble');
  const { peutAdministrer, moi: identite } = useNdfMoi();

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'notes', id],
    queryFn: () => ndfApi.notes.detail(id),
    retry: false,
  });
  const associables = useQuery({
    queryKey: ['ndf', 'notes', 'associables'],
    queryFn: () => ndfApi.notes.associables(),
    enabled: ajout,
    retry: false,
  });

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['ndf', 'notes'] });
    qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
  };

  const renommer = useMutation({
    mutationFn: () => ndfApi.notes.modifier(id, titre.trim() || null),
    onSuccess: () => {
      setEdition(false);
      rafraichir();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le titre n’a pas pu être enregistré.'),
  });

  const associer = useMutation({
    mutationFn: (ids: string[]) => ndfApi.notes.associer(id, ids),
    onSuccess: (res) => {
      toast.success(
        `${res.depenses.length} dépense${res.depenses.length > 1 ? 's' : ''} dans cette note.`,
      );
      setChoisies(new Set());
      setAjout(false);
      rafraichir();
      qc.invalidateQueries({ queryKey: ['ndf', 'notes', 'associables'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’association a échoué.'),
  });

  const dissocier = useMutation({
    mutationFn: (depenseId: string) => ndfApi.notes.dissocier(id, depenseId),
    onSuccess: () => {
      toast.success('Dépense retirée de la note.');
      rafraichir();
      qc.invalidateQueries({ queryKey: ['ndf', 'notes', 'associables'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le retrait a échoué.'),
  });

  const soumettre = useMutation({
    mutationFn: () => ndfApi.notes.soumettre(id),
    onSuccess: (res) => {
      setSoumission(false);
      toast.success(
        res.note.statut === 'attente_rh'
          ? 'Note envoyée au contrôle RH.'
          : 'Note envoyée à votre responsable.',
      );
      rafraichir();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’envoi a échoué.'),
  });

  // La facture Books est émise automatiquement à l'approbation finale. Ce
  // bouton sert quand elle a échoué — Zoho indisponible, article mal nommé —
  // et pour le premier test. Le pipeline étant idempotent, cliquer deux fois
  // ne crée pas deux factures.
  // Ramener la note au brouillon : le salarié se ravise, ou l'administration
  // débloque un circuit coincé.
  const ramener = useMutation({
    mutationFn: (quoi: 'rappeler' | 'rouvrir') =>
      quoi === 'rappeler' ? ndfApi.notes.rappeler(id) : ndfApi.notes.rouvrir(id),
    onSuccess: (res) => {
      toast.success('Note remise en brouillon. Elle devra être soumise à nouveau.');
      // Les conséquences laissées derrière sont dites une par une : c'est
      // l'administration qui juge, mais elle doit savoir ce qui reste ouvert.
      for (const a of (res as { avertissements?: string[] }).avertissements ?? []) toast.info(a);
      setConfirmationReouverture(false);
      rafraichir();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La note n’a pas pu être remise en brouillon.'),
  });

  const facturer = useMutation({
    mutationFn: () => ndfApi.notes.facturer(id),
    onSuccess: (res) => {
      toast.success(
        res.deja_existante
          ? 'La facture existait déjà : rien n’a été créé en double.'
          : `Facture créée dans Zoho Books (${res.lignes} ligne${res.lignes > 1 ? 's' : ''}).`,
      );
      rafraichir();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La facture n’a pas pu être créée.'),
  });

  const supprimer = useMutation({
    mutationFn: () => ndfApi.notes.supprimer(id),
    onSuccess: () => {
      toast.success('Note supprimée. Vos dépenses sont conservées.');
      rafraichir();
      navigate('/notes-de-frais/mes-notes');
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La suppression a échoué.'),
  });

  if (isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!data) return null;

  const { note, depenses, groupes } = data;
  const statut = STATUTS_NOTE[note.statut] ?? { libelle: note.statut, ton: 'neutre' as const };
  // Une note refusée redevient modifiable, mais seulement sur les dépenses
  // pointées : la règle est appliquée par le serveur, l'écran s'y aligne.
  const modifiable = note.statut === 'brouillon';

  const estProprietaire = identite?.salarie?.id === note.salarie_id;

  // Les alertes des dépenses de la note, dédoublonnées : le salarié n'a pas
  // besoin de lire trois fois « justificatif à risque ».
  const alertes = [
    ...new Set(
      depenses.flatMap((d) => d.alertes?.filter((a) => a.niveau !== 'bloquant').map((a) => a.message) ?? []),
    ),
  ];

  return (
    <div className="space-y-4 pb-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {edition ? (
            <div className="flex flex-wrap items-end gap-2">
              <Champ label="Titre de la note">
                <Saisie
                  value={titre}
                  placeholder="Salon de Lyon, déplacements de juillet…"
                  onChange={(e) => setTitre(e.target.value)}
                />
              </Champ>
              <BoutonPrincipal
                type="button"
                disabled={renommer.isPending}
                onClick={() => renommer.mutate()}
              >
                Enregistrer
              </BoutonPrincipal>
              <BoutonSecondaire type="button" onClick={() => setEdition(false)}>
                Annuler
              </BoutonSecondaire>
            </div>
          ) : (
            <>
              <h1 className="font-titre text-lg font-bold text-nuit">
                {note.titre || note.numero}
              </h1>
              <p className="text-sm text-nuit/60">
                {note.titre ? `${note.numero} · ` : ''}
                {note.date_note ? dateFr(note.date_note) : 'Non soumise'}
                {/* La date du versement clôt l'histoire de la note : c'est la
                    première chose qu'on cherche quand on revient dessus. */}
                {note.statut === 'remboursee' &&
                  (note.date_remboursement
                    ? ` · remboursée le ${dateFr(note.date_remboursement)}`
                    : ' · date de versement inconnue')}
              </p>
            </>
          )}
        </div>
        <BoutonSecondaire type="button" onClick={() => navigate('/notes-de-frais/mes-notes')}>
          Retour
        </BoutonSecondaire>
      </header>

      {/* Bascule d'ensemble / chronologie : la seconde répond à « que s'est-il
          passé, et dans quel ordre ? », qu'aucun autre écran ne traite. */}
      <div className="flex gap-1 rounded-lg border border-nuit/15 p-1">
        {(['ensemble', 'chronologie'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVue(v)}
            className={`min-h-11 flex-1 rounded-md px-3 text-sm ${
              vue === v ? 'bg-nuit font-semibold text-white' : 'text-nuit'
            }`}
          >
            {v === 'ensemble' ? 'Vue d’ensemble' : 'Chronologie'}
          </button>
        ))}
      </div>

      {vue === 'chronologie' && (
        <section className="rounded-lg border border-nuit/10 bg-white p-4">
          <ChronologieNote noteId={id} />
        </section>
      )}

      {vue === 'ensemble' && (
        <>
      <div className="flex flex-wrap items-center gap-1">
        <Etiquette ton={statut.ton}>{statut.libelle}</Etiquette>
        {modifiable && !edition && (
          <button
            type="button"
            onClick={() => {
              setTitre(note.titre ?? '');
              setEdition(true);
            }}
            className="min-h-11 text-sm text-nuit underline"
          >
            {note.titre ? 'Renommer' : 'Donner un titre'}
          </button>
        )}
      </div>

      {/* Les trois totaux ne disent pas la même chose, et l'écran doit le dire
          aussi : sans explication, « total » et « à rembourser » passent pour
          une redite. */}
      <section className="space-y-2 rounded-lg border border-nuit/10 bg-white p-4 text-sm">
        <Total libelle="Total de la note" valeur={note.total_ttc} fort />
        <Total
          libelle="À vous rembourser"
          valeur={note.total_a_rembourser}
          aide="Les dépenses que vous avez avancées."
        />
        <Total
          libelle="Refacturable aux clients"
          valeur={note.total_facturable}
          aide="Le HT quand la TVA est récupérable, le TTC sinon."
        />
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-titre text-sm font-bold text-nuit">
            {depenses.length} dépense{depenses.length > 1 ? 's' : ''}
          </h2>
          {modifiable && (
            <BoutonSecondaire type="button" onClick={() => setAjout((v) => !v)}>
              {ajout ? 'Fermer' : 'Ajouter des dépenses'}
            </BoutonSecondaire>
          )}
        </div>

        {depenses.length === 0 && !ajout && (
          <p className="rounded-lg border border-dashed border-nuit/25 bg-white p-6 text-center text-sm text-nuit/70">
            Cette note est vide. Ajoutez-y les dépenses que vous voulez présenter ensemble.
          </p>
        )}

        {groupes.map((g) => (
          <div key={g.cle} className="rounded-lg border border-nuit/10 bg-white p-3">
            <h3 className="mb-2 font-titre text-sm font-bold text-nuit">{g.libelle}</h3>
            <ul className="space-y-1">
              {g.depense_ids.map((did) => {
                const d = depenses.find((x) => x.id === did);
                if (!d) return null;
                return (
                  <li key={did} className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate text-nuit">
                        {d.description || 'Dépense'}
                      </span>
                      <span className="block text-nuit/60">{dateFr(d.date_depense)}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="font-medium text-nuit">{euros(d.montant_ttc)}</span>
                      {modifiable && (
                        <button
                          type="button"
                          onClick={() => dissocier.mutate(d.id)}
                          className="min-h-11 text-sm text-nuit underline"
                        >
                          Retirer
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </section>

      {ajout && (
        <PanneauAssociation
          groupes={associables.data?.groupes ?? []}
          depenses={associables.data?.depenses ?? []}
          choisies={choisies}
          onBasculer={(ids, actif) => {
            const suivant = new Set(choisies);
            for (const i of ids) (actif ? suivant.add(i) : suivant.delete(i));
            setChoisies(suivant);
          }}
          enCours={associer.isPending}
          onValider={() => associer.mutate([...choisies])}
        />
      )}

      {/* R-NDF-04 : on montre les alertes AVANT de confirmer. Les découvrir
          après coup, par un refus, coûte un aller-retour complet. */}
      {soumission && (
        <section className="space-y-3 rounded-lg border border-jaune-fond bg-white p-4">
          <h2 className="font-titre text-sm font-bold text-nuit">Avant d’envoyer</h2>
          {alertes.length > 0 ? (
            <>
              <p className="text-sm text-nuit/70">
                {alertes.length} point{alertes.length > 1 ? 's' : ''} seront visibles par votre
                valideur :
              </p>
              <ul className="space-y-1">
                {alertes.map((a, i) => (
                  <li key={i} className="text-sm text-jaune">
                    • {a}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-nuit/70">Aucun point d’attention sur cette note.</p>
          )}
          <p className="text-sm text-nuit">
            Une fois envoyée, la note n’est <strong>plus modifiable</strong> tant que votre valideur
            ne vous l’a pas renvoyée.
          </p>
          <div className="flex flex-wrap gap-2">
            <BoutonSecondaire type="button" onClick={() => setSoumission(false)}>
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal
              type="button"
              disabled={soumettre.isPending}
              onClick={() => soumettre.mutate()}
            >
              {soumettre.isPending ? 'Envoi…' : 'Confirmer l’envoi'}
            </BoutonPrincipal>
          </div>
        </section>
      )}

      {modifiable && !soumission && depenses.length > 0 && (
        <BoutonPrincipal type="button" onClick={() => setSoumission(true)} className="w-full">
          Soumettre pour approbation
        </BoutonPrincipal>
      )}

      {/* Réservée à l'ADMINISTRATION : la facture fournisseur relève de la
          comptabilité, pas du salarié ni de son valideur. Le serveur applique
          la même règle : masquer sans interdire ne protège rien. */}
      {peutAdministrer && ['a_rembourser', 'cloturee', 'remboursee'].includes(note.statut) && (
        <section className="rounded-lg border border-nuit/10 bg-white p-4">
          <h2 className="mb-1 font-titre text-sm font-bold text-nuit">Facture fournisseur</h2>
          <p className="mb-2 text-sm text-nuit/70">
            {note.zoho_bill_id
              ? 'La facture de cette note existe dans Zoho Books.'
              : 'Aucune facture n’a encore été créée pour cette note.'}
          </p>
          <BoutonSecondaire
            type="button"
            disabled={facturer.isPending}
            onClick={() => facturer.mutate()}
          >
            {facturer.isPending
              ? 'Création…'
              : note.zoho_bill_id
                ? 'Vérifier la facture'
                : 'Créer la facture dans Books'}
          </BoutonSecondaire>
        </section>
      )}

      {modifiable && (
        <div className="flex flex-wrap gap-2">
          {confirmation ? (
            <>
              <BoutonSecondaire type="button" onClick={() => setConfirmation(false)}>
                Annuler
              </BoutonSecondaire>
              <button
                type="button"
                disabled={supprimer.isPending}
                onClick={() => supprimer.mutate()}
                className="min-h-11 rounded-lg bg-corail px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                Confirmer la suppression
              </button>
            </>
          ) : (
            // La suppression est irréversible : elle se confirme. Les dépenses,
            // elles, sont conservées — on le dit ici, avant de cliquer.
            <BoutonSecondaire type="button" onClick={() => setConfirmation(true)}>
              Supprimer la note (les dépenses sont conservées)
            </BoutonSecondaire>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}

function Total({
  libelle,
  valeur,
  aide,
  fort,
}: {
  libelle: string;
  valeur: number;
  aide?: string;
  fort?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
      <span>
        <span className={fort ? 'font-medium text-nuit' : 'text-nuit/60'}>{libelle}</span>
        {aide && <span className="block text-xs text-nuit/50">{aide}</span>}
      </span>
      <span className={`text-right ${fort ? 'font-titre font-bold text-nuit' : 'font-medium text-nuit'}`}>
        {euros(valeur)}
      </span>
    </div>
  );
}
